/* eslint-disable no-use-before-define */
import { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  MaskedInput,
  TextInput,
  Checkbox,
} from 'uxd-design-system-components';
import isEmail from 'validator/lib/isEmail';
import AddContactFormModalProps from '~types/components/AddContactFormModalProps';
import getText from '../../assets/locale/locale';
import LocaleContext from '../../contexts/LocaleContext';
import { LOCALE_FR_CA } from '../../constants/constants';
import './AddContactFormModal.scss';

const AddContactFormModal = ({
  displayColdContactModal,
  setDisplayColdContactModal,
  emailResultsToFA,
  coldContactData,
  setColdContactData,
  faInfo,
}: AddContactFormModalProps) => {
  const locId = useContext(LocaleContext);
  const nameRegex = /^[a-zA-ZÀ-ÿ ‘’´'-]*$/;
  // this regex will match strings that contain all white spaces (space, tab, etc.)
  const whiteSpaceRegex = /^\s*$/;
  // this regex will match strings that contain all digits;
  const digitsRegex = /[^0-9]/g;
  const faFullName = `${faInfo.faFirstName} ${faInfo.faLastName}`;
  const defaultText = getText(locId, 'defaultText', 'contactFormModal');
  const faHeadshotImage = faInfo.faPhoto;

  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [localFirst, setLocalFirst] = useState('');
  const [localLast, setLocalLast] = useState('');
  const [localEmail, setLocalEmail] = useState('');
  const [localPhone, setLocalPhone] = useState('');
  const [isConsentCheckboxSelected, setIsConsentCheckboxSelected] =
    useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [isEmailMeCheckboxSelected, setIsEmailMeCheckboxSelected] =
    useState(false);

  const firstNameMissingError = getText(
    locId,
    'missingFirstNameError',
    'contactFormModal'
  );

  const lastNameMissingError = getText(
    locId,
    'missingLastNameError',
    'contactFormModal'
  );

  const invalidFirstNameError =
    getText(locId, 'validFirstNameError', 'contactFormModal') +
    getText(locId, 'validNameChars', 'contactFormModal');

  const invalidLastNameError =
    getText(locId, 'validLastNameError', 'contactFormModal') +
    getText(locId, 'validNameChars', 'contactFormModal');

  const invalidEmailError = getText(locId, 'emailError', 'contactFormModal');

  const missingEmailError = getText(
    locId,
    'missingEmailError',
    'contactFormModal'
  );

  const invalidPhoneError = getText(locId, 'phoneError', 'contactFormModal');

  const customPhoneMask = getText(locId, 'phoneMask', 'contactFormModal');

  const customPhoneMaskHelperText = getText(
    locId,
    'phoneMaskHelperText',
    'contactFormModal'
  );

  const validateFirstName = () => {
    if (localFirst.length === 0 || whiteSpaceRegex.test(localFirst)) {
      setFirstNameError(firstNameMissingError);
      return false;
    }
    if (!nameRegex.test(localFirst)) {
      setFirstNameError(invalidFirstNameError);
      return false;
    }
    setLocalFirst(localFirst);
    setFirstNameError('');
    setColdContactData({
      ...coldContactData,
      firstName: localFirst.trim(),
    });
    return true;
  };

  const validateLastName = () => {
    if (localLast.length === 0 || whiteSpaceRegex.test(localLast)) {
      setLastNameError(lastNameMissingError);
      return false;
    }
    if (!nameRegex.test(localLast)) {
      setLastNameError(invalidLastNameError);
      return false;
    }
    setLocalLast(localLast);
    setLastNameError('');
    setColdContactData({
      ...coldContactData,
      lastName: localLast.trim(),
    });
    return true;
  };

  const isInValidEmail = (value: string) => value !== '' && !isEmail(value);

  const isInvalidPhone = (value: string) => value !== '' && value.length !== 10;

  const isValidEmailAndPhoneCombination = () => {
    const unmaskedPhoneNumber = localPhone.replace(digitsRegex, '');
    if (localEmail === '' && unmaskedPhoneNumber === '') {
      setEmailError(invalidEmailError);
      setPhoneError(invalidPhoneError);
      return false;
    }
    if (isInValidEmail(localEmail) && unmaskedPhoneNumber === '') {
      setEmailError(invalidEmailError);
      setPhoneError('');
      return false;
    }
    if (localEmail === '' && isInvalidPhone(unmaskedPhoneNumber)) {
      setEmailError('');
      setPhoneError(invalidPhoneError);
      return false;
    }
    if (isEmail(localEmail) && isInvalidPhone(unmaskedPhoneNumber)) {
      setEmailError('');
      setPhoneError(invalidPhoneError);
      return false;
    }
    if (isInValidEmail(localEmail) && unmaskedPhoneNumber.length === 10) {
      setEmailError(invalidEmailError);
      setPhoneError('');
      return false;
    }
    if (isInValidEmail(localEmail) && isInvalidPhone(unmaskedPhoneNumber)) {
      setEmailError(invalidEmailError);
      setPhoneError(invalidPhoneError);
      return false;
    }
    if (localEmail === '' && isEmailMeCheckboxSelected) {
      setEmailError(missingEmailError);
      setPhoneError('');
      return false;
    }

    setLocalEmail(localEmail);
    setEmailError('');
    if (localPhone === '(') {
      setLocalPhone('');
    } else {
      setLocalPhone(localPhone);
    }
    setPhoneError('');

    setColdContactData({
      ...coldContactData,
      email: localEmail === '' ? null : localEmail.trim(),
      phoneNumber:
        unmaskedPhoneNumber === '' ? null : unmaskedPhoneNumber.trim(),
    });

    return true;
  };

  const isConsentChecked = () => {
    if (!isConsentCheckboxSelected) {
      setShowConsentError(true);
      return false;
    }
    setShowConsentError(false);
    return true;
  };

  const shareButtonHandler = () => {
    let isValidForm = true;
    if (!validateFirstName()) {
      isValidForm = false;
    }
    if (!validateLastName()) {
      isValidForm = false;
    }
    if (!isValidEmailAndPhoneCombination()) {
      isValidForm = false;
    }
    if (!isConsentChecked()) {
      isValidForm = false;
    }

    if (isValidForm) {
      setDisplayColdContactModal(false);
      emailResultsToFA();
    }
  };

  const consentCheckboxHandler = () => {
    let consentToFAContactTimestamp;
    if (!isConsentCheckboxSelected) {
      consentToFAContactTimestamp = new Date().toISOString();
      setIsConsentCheckboxSelected(true);
      setShowConsentError(false);
    } else {
      consentToFAContactTimestamp = null;
      setIsConsentCheckboxSelected(false);
    }
    setColdContactData({ ...coldContactData, consentToFAContactTimestamp });
  };

  const emailMeCheckboxHandler = () => {
    let emailResultsToContactTimestamp;
    if (!isEmailMeCheckboxSelected) {
      emailResultsToContactTimestamp = new Date().toISOString();
      setIsEmailMeCheckboxSelected(true);
    } else {
      emailResultsToContactTimestamp = null;
      setIsEmailMeCheckboxSelected(false);
    }
    setColdContactData({ ...coldContactData, emailResultsToContactTimestamp });
  };

  const headerTextHandler = () => {
    const baseHeaderText = getText(locId, 'header', 'contactFormModal');
    let headerText;
    if (faInfo.faFirstName && faInfo.faLastName) {
      headerText = baseHeaderText.replace('%FA_NAME%', faFullName);
    } else {
      headerText = baseHeaderText.replace('%FA_NAME%', defaultText);
    }
    return headerText;
  };

  const buttons = [
    {
      text: getText(locId, 'cancelButton', 'contactFormModal'),
      onClick: () => setDisplayColdContactModal(false),
      styleVariant: 'secondary' as const,
    },
    {
      text: getText(locId, 'shareResultsButton', 'contactFormModal'),
      onClick: () => shareButtonHandler(),
      styleVariant: 'primary' as const,
    },
  ];

  return (
    <Dialog
      title=""
      open={displayColdContactModal}
      buttons={buttons}
      className="contactFaModal"
      onRequestClose={() => setDisplayColdContactModal(false)}
    >
      {' '}
      <div className="title-container">
        {faHeadshotImage ? (
          <div className="fa-headshot">
            <img
              src={`data:image/png;base64,${faHeadshotImage}`}
              alt=""
              role="presentation"
              data-testid="fa-headshot"
            />
          </div>
        ) : null}
        <h3>{headerTextHandler()}</h3>
      </div>
      <div>
        <p className="modal-paragraph">
          {faInfo.faFirstName && faInfo.faLastName
            ? getText(locId, 'paragraphText', 'contactFormModal').replace(
                '%FA_NAME%',
                faFullName
              )
            : getText(locId, 'paragraphText', 'contactFormModal').replace(
                '%FA_NAME%',
                defaultText
              )}
        </p>
      </div>
      <form onSubmit={shareButtonHandler}>
        <TextInput
          id="first-name"
          label={getText(locId, 'firstNameLabel', 'contactFormModal')}
          name="first-name"
          value={localFirst}
          type="text"
          onChange={(text: string) => {
            setLocalFirst(text);
            setColdContactData({
              ...coldContactData,
              firstName: text.trim(),
            });
          }}
          onBlur={validateFirstName}
          error={firstNameError}
        />
        <TextInput
          id="last-name"
          label={getText(locId, 'lastNameLabel', 'contactFormModal')}
          name="last-name"
          value={localLast}
          type="text"
          onChange={(text: string) => {
            setLocalLast(text);
            setColdContactData({
              ...coldContactData,
              lastName: text.trim(),
            });
          }}
          onBlur={validateLastName}
          error={lastNameError}
        />
        <TextInput
          id="email"
          label={getText(locId, 'emailLabel', 'contactFormModal')}
          name="email"
          value={localEmail}
          type={
            locId.toLowerCase() === LOCALE_FR_CA.toLowerCase()
              ? 'text'
              : 'email'
          }
          onChange={(text: string) => {
            setLocalEmail(text);
            setColdContactData({
              ...coldContactData,
              email: text === '' ? null : text.trim(),
            });
          }}
          onBlur={() => {
            setColdContactData({
              ...coldContactData,
              email: localEmail === '' ? null : localEmail.trim(),
            });
          }}
          error={emailError}
        />
        <MaskedInput
          id="phone"
          label={getText(locId, 'phoneLabel', 'contactFormModal')}
          name="phone"
          customMask={customPhoneMask}
          customMaskHelperText={customPhoneMaskHelperText}
          value={localPhone}
          onChange={(text: string) => {
            setLocalPhone(text);
            const unmaskedPhoneNumber = text.replace(digitsRegex, '');
            setColdContactData({
              ...coldContactData,
              phoneNumber:
                unmaskedPhoneNumber === '' ? null : unmaskedPhoneNumber.trim(),
            });
          }}
          onBlur={() => {
            const unmaskedPhoneNumber = localPhone.replace(digitsRegex, '');
            setColdContactData({
              ...coldContactData,
              phoneNumber:
                unmaskedPhoneNumber === '' ? null : unmaskedPhoneNumber.trim(),
            });
          }}
          error={phoneError}
        />
        <div className={showConsentError ? 'consent-checkbox-error' : ''}>
          <Checkbox
            checked={isConsentCheckboxSelected}
            id="consent"
            name="consent"
            label={getText(locId, 'consentLabel', 'contactFormModal')}
            onChange={() => {
              consentCheckboxHandler();
            }}
          />
        </div>
        {showConsentError ? (
          <p className="consent-error">
            {getText(locId, 'consentError', 'contactFormModal')}
          </p>
        ) : null}
        <Checkbox
          checked={isEmailMeCheckboxSelected}
          id="emailMe"
          name="emailMe"
          label={getText(locId, 'emailMeLabel', 'contactFormModal')}
          onChange={() => {
            emailMeCheckboxHandler();
          }}
        />
      </form>
      <div>
        <p className="add-phone-or-email-requirement">
          {getText(locId, 'requiredLabel', 'contactFormModal')}
        </p>
        <p className="modalFooter">
          {getText(locId, 'footerText', 'contactFormModal')}
        </p>
      </div>
    </Dialog>
  );
};

AddContactFormModal.propTypes = {
  displayColdContactModal: PropTypes.bool.isRequired,
  setDisplayColdContactModal: PropTypes.func.isRequired,
  emailResultsToFA: PropTypes.func.isRequired,
  faInfo: PropTypes.shape({
    faNo: PropTypes.string,
    branchNo: PropTypes.string,
    faFirstName: PropTypes.string,
    faLastName: PropTypes.string,
    faPrimaryEmail: PropTypes.string,
    faTeamEmail: PropTypes.string,
    faPhoto: PropTypes.string,
  }).isRequired,
  coldContactData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  setColdContactData: PropTypes.func.isRequired,
};

AddContactFormModal.defaultProps = {
  coldContactData: null,
};

export default AddContactFormModal;
